/**
 * Display-only formatters. None of these mutate stored values.
 */

/**
 * Standardize a UAE/international phone number to "+971 XX XXX XXXX" style.
 *
 * Inputs we handle:
 *   "+971585010979"       → "+971 58 501 0979"
 *   "971585010979"        → "+971 58 501 0979"
 *   "00971585010979"      → "+971 58 501 0979"
 *   "+971 58 5010979"     → "+971 58 501 0979"
 *   "+12025551234"        → "+1 202 555 1234"   (US, generic split)
 *   "+44 7700 900123"     → "+44 7700 900123"   (UK, kept as-is)
 *   "+971585****"         → "+971 58 5****"     (masked — preserved if asterisks present)
 *   ""                    → ""
 *
 * Spec: display-only — never persist the formatted string back to the DB.
 * Sherzod confirmed 2026-05-02: "phones always need to be fixed and standardized,
 * and then update it over there" — interpreted as display-time normalization.
 */
export function formatPhone(input: string | null | undefined): string {
  if (!input) return '';
  const raw = String(input).trim();
  if (!raw) return '';

  // Preserve masked numbers (have at least one *) — don't try to re-group.
  if (raw.includes('*')) return raw;

  // Strip everything that isn't a digit, but keep leading + for later restore.
  const hasPlus = raw.startsWith('+');
  let digits = raw.replace(/\D/g, '');
  if (!digits) return raw;

  // Drop a leading "00" trunk (e.g. "00971...") — replace with "+".
  if (digits.startsWith('00')) digits = digits.slice(2);

  // UAE special case: "+971 XX XXX XXXX" (12 digits including the 971 country code).
  if (digits.length === 12 && digits.startsWith('971')) {
    const cc = digits.slice(0, 3);
    const a = digits.slice(3, 5);
    const b = digits.slice(5, 8);
    const c = digits.slice(8, 12);
    return `+${cc} ${a} ${b} ${c}`;
  }

  // UAE without leading country code (10 digits starting with 0 or 5):
  // "0585010979" → "+971 58 501 0979"; "585010979" → "+971 58 501 0979"
  if (digits.length === 10 && digits.startsWith('0')) {
    digits = '971' + digits.slice(1);
    return formatPhone('+' + digits);
  }
  if (digits.length === 9 && /^[5][0-9]{8}$/.test(digits)) {
    digits = '971' + digits;
    return formatPhone('+' + digits);
  }

  // Generic E.164: "+CC AAA BBB CCCC" — split last 4 + middle 3 + middle 3 + remaining as country.
  if (digits.length >= 8 && digits.length <= 15) {
    const tail = digits.slice(-4);
    const mid = digits.slice(-7, -4);
    const ar = digits.slice(-10, -7);
    const cc = digits.slice(0, -10);
    if (cc) return `+${cc} ${ar} ${mid} ${tail}`;
    return `${ar} ${mid} ${tail}`;
  }

  // Fallback: return whatever we got, with leading + preserved if present
  return hasPlus ? '+' + digits : digits;
}

/**
 * Strip a phone number to digits only (for tel: / wa.me: links).
 * Preserves leading +.
 */
export function phoneToDigits(input: string | null | undefined): string {
  if (!input) return '';
  return String(input).replace(/(?!^\+)\D/g, '');
}
